import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// "Before you submit" Support Topic deflection on the New Support Ticket form.
//
// When the agent picks a category we fetch the published Support Topics tied
// to it (SupportTicketsController#category_topics) and render them as an
// inline accordion so they can self-serve before opening a ticket. The
// callout stays hidden for a blank category or when the category has no
// matching topics.
//
// Sits alongside ticket-context-fields on the same category <select>; both
// react to the change event independently.
export default class extends Controller {
  static targets = ["category", "container", "list"]
  static values = { categoryTopicsUrl: String }

  // Tracks the category whose topics we're currently fetching so a stale (out-
  // of-order) response from a previous selection can be discarded.
  #pendingCategory = null

  connect() {
    this.categoryChanged()
  }

  async categoryChanged() {
    const category = this.categoryTarget.value

    if (!category) {
      this.#hide()
      return
    }

    this.#pendingCategory = category
    const response = await get(this.categoryTopicsUrlValue, {
      query: { category },
      responseKind: "json",
    })
    // Discard a stale response if the selection changed while this was in flight.
    if (this.#pendingCategory !== category) return
    if (!response.ok) {
      this.#hide()
      return
    }

    const topics = await response.json
    if (this.#pendingCategory !== category) return

    this.#render(topics)
  }

  #render(topics) {
    if (!topics || topics.length === 0) {
      this.#hide()
      return
    }

    // solution_html is sanitized server-side through the SOC-2 allowlist
    // (SupportTopicsHelper#sanitize_topic_solution, applied in
    // SupportTicketsController#category_topics) — do NOT inject any other
    // server field via innerHTML without the same treatment. The title is
    // escaped here since it is inserted as markup.
    this.listTarget.innerHTML = topics
      .map(
        (topic) => `
          <div class="gl-accordion-item">
            <button type="button" class="gl-accordion-header" aria-expanded="false" data-action="click->accordion#toggle">
              <span>${this.#escape(topic.title)}</span>
            </button>
            <div class="gl-accordion-body">${topic.solution_html || ""}</div>
          </div>`,
      )
      .join("")
    this.containerTarget.classList.remove("d-none")
  }

  #hide() {
    this.#pendingCategory = null // invalidate any in-flight fetch
    this.containerTarget.classList.add("d-none")
    this.listTarget.innerHTML = ""
  }

  #escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
